import { useCallback } from 'react';
import { IAuditLogEntry } from '@skystone/shared';
import { auditApi } from '@/services/audit-api';
import { formatDateTime } from '@/lib/date-utils';
import { formatAuditValue } from '@/lib/audit-utils';
import { Badge, PageLoading, ErrorBanner } from '@/components/ui';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import { useServerPaginatedQuery } from '@/hooks/useServerPaginatedQuery';

const DEFAULT_PAGE_SIZE = 20;

const columns: ColumnDef<IAuditLogEntry>[] = [
  {
    id: 'createdAt',
    header: 'Timestamp',
    accessor: 'createdAt',
    cell: (val) => formatDateTime(val as string),
  },
  {
    id: 'actorUsername',
    header: 'Acting User',
    accessor: 'actorUsername',
    filter: { type: 'text', placeholder: 'Filter user...' },
  },
  {
    id: 'eventType',
    header: 'Event Type',
    accessor: 'eventType',
    cell: (val) => (
      <Badge variant="secondary">{val as string}</Badge>
    ),
    filter: {
      type: 'select',
      options: [
        { label: 'CREATE_USER', value: 'CREATE_USER' },
        { label: 'DELETE_USER', value: 'DELETE_USER' },
        { label: 'UPDATE_USER_ROLE', value: 'UPDATE_USER_ROLE' },
        { label: 'UPDATE_JIRA_MAPPING', value: 'UPDATE_JIRA_MAPPING' },
        { label: 'auth.login', value: 'auth.login' },
      ],
    },
  },
  {
    id: 'entityName',
    header: 'Entity',
    accessor: 'entityName',
  },
  {
    id: 'oldValue',
    header: 'From',
    accessor: 'oldValue',
    cell: (val) => {
      const value = val as Record<string, unknown> | null;
      return (
        <span className="max-w-xs truncate text-xs text-muted-foreground" title={value ? JSON.stringify(value) : '—'}>
          {formatAuditValue(value)}
        </span>
      );
    },
  },
  {
    id: 'newValue',
    header: 'To',
    accessor: 'newValue',
    cell: (val) => {
      const value = val as Record<string, unknown> | null;
      return (
        <span className="max-w-xs truncate text-xs text-muted-foreground" title={value ? JSON.stringify(value) : '—'}>
          {formatAuditValue(value)}
        </span>
      );
    },
  },
];

export function AuditLogTab() {
  const fetcher = useCallback(
    ({ page, pageSize, filters }: { page: number; pageSize: number; filters: Record<string, string> }) =>
      auditApi.getLogs({
        page,
        pageSize,
        eventType: filters.eventType || undefined,
        actorUsername: filters.actorUsername || undefined,
      }),
    [],
  );

  const { data, loading, error, serverPagination, handleFilterChange, showInitialLoading } =
    useServerPaginatedQuery(fetcher, DEFAULT_PAGE_SIZE, 'Failed to load audit logs');

  if (showInitialLoading) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        View all auditable events across the system.
      </p>

      <ErrorBanner message={error} />

      <DataTable
        data={data}
        columns={columns}
        keyExtractor={(entry) => entry.id}
        isLoading={loading}
        emptyMessage="No audit log entries found."
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        enablePagination={false}
        serverPagination={serverPagination}
        enableFilters={true}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
}
